import {
  WHATSAPP_ACCOUNT_KEY,
  WHATSAPP_OPTIONS_KEY,
  mergeWhatsappAccountSettings,
  mergeWhatsappNotificationSettings,
} from './whatsappNotificationSettings.js';

export const DEFAULT_CAN_ACCESS = {
  shipment: true,
  attendance: false,
  attendance_manual: false,
  attendance_qr: false,
  inventory: false,
  loans: true,
  expenses: true,
  whatsapp_notification: false,
  email_notification: false,
  commission: false,
  on_contract: false,
  on_contract_truck: false,
  cashbook: false,
  my_partnerships: false,
  analytical_reports: true,
  payment_gateway: false,
  ewaybill: false,
  gst_verification: false,
  login_otp: false,
  agent_network: false,
  external_shipment: false,
  external_ml: false,
  external_commission: false,
  external_on_contract: false,
  [WHATSAPP_OPTIONS_KEY]: mergeWhatsappNotificationSettings(null),
  [WHATSAPP_ACCOUNT_KEY]: mergeWhatsappAccountSettings(null),
};

/** Shown in Core Feature Access grid (integrations moved to separate section). */
export const CORE_FEATURES = [
  'shipment',
  'attendance',
  'inventory',
  'loans',
  'expenses',
  'commission',
  'on_contract',
  'on_contract_truck',
  'cashbook',
  'my_partnerships',
  'analytical_reports',
  'login_otp',
];

/** Collapsible integration panels on company Add / Edit / View. */
export const INTEGRATION_FEATURES = [
  'email_notification',
  'whatsapp_notification',
  'payment_gateway',
  'ewaybill',
  'gst_verification',
];

export const AGENT_NETWORK_FEATURES = [
  'agent_network',
  'external_shipment',
  'external_ml',
  'external_commission',
  'external_on_contract',
];

export const FEATURE_LABELS = {
  shipment: 'Shipment',
  attendance: 'Attendance',
  inventory: 'Inventory',
  loans: 'Loans',
  expenses: 'Expenses',
  whatsapp_notification: 'WhatsApp Notification',
  email_notification: 'Email Notification',
  commission: 'Shipment Commission',
  on_contract: 'On Contract',
  on_contract_truck: 'On Contract Truck',
  cashbook: 'Cashbook',
  my_partnerships: 'My Partnerships',
  analytical_reports: 'Analytical Reports',
  login_otp: 'Login OTP (WhatsApp)',
  payment_gateway: 'Payment Gateway',
  ewaybill: 'E-Way Bill API',
  gst_verification: 'GST Verification',
  attendance_manual: 'Manual',
  attendance_qr: 'QR',
  agent_network: 'Agent Network',
  external_shipment: 'External Shipment',
  external_ml: 'External ML Sheet',
  external_commission: 'External Shipment Commission',
  external_on_contract: 'External On Contract',
};

export function isCompanyDeactivated(company) {
  return (company?.status || '').toLowerCase() === 'inactive';
}

export function companySelectLabel(company) {
  const base = `${company.company_name} (${company.company_id})`;
  return isCompanyDeactivated(company) ? `${base} — Inactive` : base;
}

export const NETWORK_MODE_LABELS = {
  INTERNAL: 'Company Owner',
  EXTERNAL: 'Agent',
};

export function mergeAttendanceMethods(canAccess) {
  const source = canAccess || {};
  const attendanceOn = Boolean(source.attendance);
  const hasManual = Object.prototype.hasOwnProperty.call(source, 'attendance_manual');
  const hasQr = Object.prototype.hasOwnProperty.call(source, 'attendance_qr');
  return {
    attendance_manual: hasManual ? Boolean(source.attendance_manual) : attendanceOn,
    attendance_qr: hasQr ? Boolean(source.attendance_qr) : attendanceOn,
  };
}

export function mergeCanAccess(canAccess) {
  const source = canAccess || {};
  const merged = {
    ...DEFAULT_CAN_ACCESS,
    ...source,
    ...mergeAttendanceMethods(source),
  };
  merged[WHATSAPP_OPTIONS_KEY] = mergeWhatsappNotificationSettings(
    source[WHATSAPP_OPTIONS_KEY]
  );
  merged[WHATSAPP_ACCOUNT_KEY] = mergeWhatsappAccountSettings(
    source[WHATSAPP_ACCOUNT_KEY]
  );
  return merged;
}

export function applyAttendanceFeatureState(canAccess, attendanceEnabled) {
  const next = {
    ...canAccess,
    attendance: Boolean(attendanceEnabled),
  };
  if (!attendanceEnabled) {
    next.attendance_manual = false;
    next.attendance_qr = false;
    return next;
  }
  if (!canAccess.attendance_manual && !canAccess.attendance_qr) {
    next.attendance_manual = true;
    next.attendance_qr = true;
  }
  return next;
}

export function getAttendanceCanAccessReset() {
  return {
    attendance: false,
    attendance_manual: false,
    attendance_qr: false,
  };
}

export function formatFeatureLabel(featureKey) {
  return FEATURE_LABELS[featureKey] || featureKey.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

export function getAgentNetworkCanAccessReset() {
  return {
    agent_network: false,
    external_shipment: false,
    external_ml: false,
    external_commission: false,
    external_on_contract: false,
  };
}

export function isExternalNetworkCompany(company) {
  return company?.network_mode === 'EXTERNAL';
}

export const DEFAULT_USER_LIMIT_BY_NETWORK_MODE = {
  INTERNAL: '5',
  EXTERNAL: '1',
};

export function getDefaultUserLimitForNetworkMode(networkMode) {
  return DEFAULT_USER_LIMIT_BY_NETWORK_MODE[networkMode] || DEFAULT_USER_LIMIT_BY_NETWORK_MODE.INTERNAL;
}
